import _ from 'lodash';
import React, { useEffect, useState } from 'react';
import { gql, useApolloClient, useMutation, useQuery } from '@apollo/client';

// Components
import { ManageEvaluationsContainer } from './manage-evaluations.container';
import { getIsSavingScores } from '../../_core/OfflineHandler/offline-handler.container';
import { useRefetchAfterOfflineSync } from '../../../lib/services/offline-sync.service';
import { IRecordUserSessionData, useFlushScoresOnUnmount, useSubmitScores } from '../../../lib/services/score-submit.service';
import { getAuthUser } from '../../../lib/services/auth.service';
import { RECORD_USER_SESSION } from '../../../lib/queries/user-session.queries';
import { noteScoredSession } from '../../../lib/utils/cache-persistence';

// Data
// Exported for manage-evaluations.scope-fields.test.ts, which pins the gate's vouch fields.
export const GET_MANAGE_EVALUATIONS = gql`
query GetManageEvaluations($assessmentSessionId: ID!) {
  assessmentSession(_id: $assessmentSessionId) {
    _id
    location
    area
    address
    date
    start
    assessmentId
    ageGroup {
      _id
      name
      assessmentId
    }
    practicePlans {
      _id
      drills {
        _id
        name
        image
        deletedAt
        skillSets {
          positionIds
          skills {
            _id
            name
            type
            options
            deletedAt
          }
        }
      }
    }
    drills {
      _id
      name
      image
      deletedAt
      skillSets {
        positionIds
        skills {
          _id
          name
          type
          options
          deletedAt
        }
      }
    }
    checkedInPlayers {
      _id
      position {
        _id
        name
      }
      ageGroup {
        _id
        assessmentId
      }
      player {
        _id
        firstName
        lastName
        nickname
      }
    }
    jerseys
  }
}
`;

const GET_MANAGE_EVALUATION_SCORES = gql`
  query GetManageEvaluationScores($assessmentSessionId: ID!) {
    evaluationScores(assessmentSessionId: $assessmentSessionId) {
      _id
      raw
      type
      playerAssessmentId
      drillId
      skillId
      assessmentId
      ageGroupId
      assessmentSessionId
      round
    }
  }
`;

interface IManageEvaluationsGraphQL {
  assessmentSessionId: string;
}

export const ManageEvaluationsGraphQL: React.FC<IManageEvaluationsGraphQL> = ({ assessmentSessionId }) => {
  const [hasUnsavedScores, setHasUnsavedScores] = useState(false);
  const isSaving = getIsSavingScores();
  const user = getAuthUser();
  const { data, loading: loadingEvaluations } = useQuery(GET_MANAGE_EVALUATIONS, {
    variables: { assessmentSessionId },
    fetchPolicy: 'network-only',
    nextFetchPolicy: 'cache-and-network'
  });

  const { data: scoresData, loading: loadingScores, called: scoresCalled, refetch: refetchScores } = useQuery(GET_MANAGE_EVALUATION_SCORES, {
    variables: { assessmentSessionId },
    fetchPolicy: 'network-only',
    nextFetchPolicy: 'cache-and-network'
  });

  const assessmentSession = data && data.assessmentSession;
  const players = (data && data.assessmentSession.checkedInPlayers) || [];
  const jerseys = (data && data.assessmentSession.jerseys) || {};
  const scores = (scoresData && scoresData.evaluationScores) || [];
  let drills = (data && data.assessmentSession.drills) || [];
  let practicePlans = (data && data.assessmentSession.practicePlans) || [];

  practicePlans.forEach((plan: IPracticePlan) => {
    drills = drills.concat(plan.drills || []);
  });
  drills = drills.filter((d: IDrill) => !d.deletedAt)
  drills = _.uniqBy(drills, '_id');

  const [recordUserSessionActionMutation] = useMutation<IRecordUserSessionData>(RECORD_USER_SESSION);

  // Marks this session's scores as worth keeping and evicts older sessions' scores,
  // which are re-fetched on demand. Keeps the cache cheap to serialize on every persist.
  const client = useApolloClient();
  useEffect(() => { noteScoredSession(client.cache, assessmentSessionId); }, [client, assessmentSessionId]);

  useRefetchAfterOfflineSync(refetchScores, scoresCalled, loadingScores);

  useFlushScoresOnUnmount(user, recordUserSessionActionMutation);
  const handleSubmitScores = useSubmitScores(user, recordUserSessionActionMutation, refetchScores, 'Failed submitting scores');

  return <ManageEvaluationsContainer
    assessmentSession={assessmentSession}
    drills={drills}
    players={players}
    jerseys={jerseys}
    scores={scores}
    submitScores={handleSubmitScores}
    loading={loadingEvaluations || loadingScores}
    hasUnsavedScores={hasUnsavedScores}
    setHasUnsavedScores={setHasUnsavedScores}
    isSaving={isSaving}
  />;
};
